import { Injectable } from '@angular/core';
@Injectable({ providedIn: 'root' })
export class AddressUtilsService {
	/**
	 * Resolves the display-friendly city name based on Google Places address components.
	 *
	 * Priority:
	 *  1. neighborhood (e.g., Jamaica)
	 *  2. sublocality_level_1 (e.g., Queens, Brooklyn)
	 *  3. locality (e.g., New York, Los Angeles)
	 *  4. administrative_area_level_3 (e.g., Castleton Township)
	 *
	 * @param components The addressComponents array from Google Places API
	 * @returns Best-fit display city string or null
	 */
	resolveDisplayCity(
    addressComponents: any[],
    formattedAddress: string
  ): string | null {
    if (!Array.isArray(addressComponents) || !formattedAddress) return null;

    const isNewYorkState = addressComponents.some(
      (comp) =>
        comp.types.includes("administrative_area_level_1") &&
        (comp.shortText === "NY" || comp.longText === "New York")
    );

    const nyPriority = ["neighborhood", "locality", "sublocality_level_1", "administrative_area_level_3"];
    const defaultPriority = ["locality", "sublocality_level_1", "neighborhood", "administrative_area_level_3"];

    const priorityTypes = isNewYorkState ? nyPriority : defaultPriority;

    const formattedLower = formattedAddress.toLowerCase();

    const routeNames = addressComponents
    .filter((comp) => comp.types.includes("route"))
    .reduce((acc: string[], comp) => {
			if (comp.longText) acc.push(comp.longText.toLowerCase());
			if (comp.shortText) acc.push(comp.shortText.toLowerCase());
			return acc;
		}, [])
		.filter(Boolean);

    const isPartOfRoute = (value: string | undefined): boolean => {
      if (!value) return false;
      const word = value.toLowerCase().trim();
      return routeNames.some(route => {
        const regex = new RegExp(`\\b${word}\\b`, 'i');
        return regex.test(route ?? '');
      });
    };

    const containsWord = (value: string): boolean => {
      if (!value) return false;
      const escaped = value.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
      const regex = new RegExp(`\\b${escaped}\\b`, "i");
      return regex.test(formattedLower);
    };
    

    // Try to find the component that matches both type and formattedAddress
    for (const type of priorityTypes) {
      const match = addressComponents.find(
        (comp) => {
          return comp.types.includes(type) && (
            (containsWord(comp.longText.toLowerCase()) ||
            containsWord(comp.shortText.toLowerCase())) &&
            !isPartOfRoute(comp.longText) && !isPartOfRoute(comp.shortText)
          )
        }    
      );
      if (match) {
        const matchedValue = containsWord(match.longText) ? match.longText : match.shortText;
        return matchedValue;
      }
    }
     //Fallback — return first available component by priority
    for (const type of priorityTypes) {
      const match = addressComponents.find((comp) => comp.types.includes(type));
      if (match) return match.longText;
    }
    return null;
  }
}
